/**
 * Cap de compras mensal — utilitários puros (sem React/Supabase) para testar
 * o greedy do Sprint 1. A v1 trabalha com "valor de pedido" (total_value);
 * o Sprint 2 troca por "caixa do mês" via parcelas.
 */

export interface CapItem {
  /** Identificador estável; o caller usa para selecionar/filtrar a UI. */
  id: string
  /** Custo unitário (R$). */
  unit_cost: number
  /** Quantidade sugerida pelo usuário (já com override aplicado). */
  qty: number
  /** Posto de urgência: menor = mais urgente. Usar STATUS_ORDER.indexOf(status). */
  urgencyRank: number
  /** Curva ABC como rank: A=0, B=1, C=2, null=3 (deixa por último). */
  abcRank: number
  /** Score comercial 0–100 (desempate). */
  score: number
}

export interface CapPartition {
  /** IDs que cabem no orçamento (ordem da lista de entrada após sort). */
  fitsIds: Set<string>
  /** Valor total dos itens que cabem (R$). */
  fitsValue: number
  /** Valor total dos itens que NÃO cabem (R$) — vão para "Virada do mês". */
  overflowsValue: number
  /** Valor total da lista inteira, ignorando o cap (R$). */
  totalValue: number
  /** Quantos itens cabem vs total (para o cabeçalho "X de Y itens"). */
  fitsCount: number
  totalCount: number
}

/**
 * Greedy: ordena por (urgência, ABC, score) e acumula até estourar o saldo.
 * `budgetRemaining` é o que sobrou do orçamento do mês depois de descontar
 * os pedidos já criados — passe `Infinity` (ou um número absurdo) para
 * desativar o cap. Itens com `unit_cost*qty == 0` cabem sempre.
 */
export function splitByBudget(items: CapItem[], budgetRemaining: number): CapPartition {
  const sorted = [...items].sort((a, b) =>
    a.urgencyRank - b.urgencyRank
    || a.abcRank - b.abcRank
    || b.score - a.score
  )

  const fitsIds = new Set<string>()
  let fitsValue = 0
  let overflowsValue = 0
  let totalValue = 0
  let fitsCount = 0

  for (const it of sorted) {
    const value = it.unit_cost * it.qty
    totalValue += value
    if (value <= 0 || fitsValue + value <= budgetRemaining) {
      fitsIds.add(it.id)
      fitsValue += value
      fitsCount++
    } else {
      overflowsValue += value
    }
  }

  return {
    fitsIds,
    fitsValue,
    overflowsValue,
    totalValue,
    fitsCount,
    totalCount: items.length,
  }
}

/**
 * Soma `total_value` dos pedidos criados no mês alvo (YYYY-MM), descartando
 * status Cancelado. Usado para "quanto já comprometemos esse mês".
 */
export function monthlyPurchaseTotal(
  orders: ReadonlyArray<{ created_at: string | null; status: string; total_value: number | null }>,
  monthKey: string,
): number {
  let total = 0
  for (const po of orders) {
    if (po.status === 'Cancelado') continue
    if (!po.created_at) continue
    if (po.created_at.slice(0, 7) !== monthKey) continue
    total += Number(po.total_value) || 0
  }
  return total
}

/**
 * `true` se a data corrente já passou do dia de corte daquele mês. Útil
 * para a Sugestão pré-marcar Standby. `cutoffDay` deve estar em 1..28.
 */
export function isAfterCutoff(today: Date, cutoffDay: number): boolean {
  return today.getDate() >= cutoffDay
}

// ── Sprint 2: parcelas e caixa do mês ───────────────────────────────────────

export interface ProjectedInstallment {
  /** ISO YYYY-MM-DD do vencimento. */
  due_date: string
  amount: number
}

/**
 * Projeta as parcelas que SERIAM geradas se um PO de `total` fosse emitido
 * hoje no fornecedor com esses prazos. Espelha exatamente a lógica da função
 * SQL `regenerate_order_installments`:
 *
 *   - sem prazos → 1 parcela no próprio dia (à vista)
 *   - N prazos → N parcelas iguais (round 2 casas); a última absorve o resto
 *     para fechar o `total` sem perda de centavo
 *   - terms negativos/0 são descartados (tratados como inválidos)
 */
export function projectInstallments(total: number, terms: number[], today: Date): ProjectedInstallment[] {
  if (!Number.isFinite(total) || total <= 0) return []
  const validTerms = terms.filter(t => Number.isFinite(t) && t > 0)
  const dayMs = 86_400_000
  const fmt = (d: Date) => d.toISOString().slice(0, 10)

  if (validTerms.length === 0) {
    return [{ due_date: fmt(today), amount: total }]
  }

  const n = validTerms.length
  const per = Math.round((total / n) * 100) / 100
  const out: ProjectedInstallment[] = []
  let acc = 0
  for (let i = 0; i < n; i++) {
    const due = fmt(new Date(today.getTime() + validTerms[i] * dayMs))
    const isLast = i === n - 1
    const amount = isLast ? Math.round((total - acc) * 100) / 100 : per
    if (!isLast) acc += per
    out.push({ due_date: due, amount })
  }
  return out
}

/**
 * Soma das parcelas que caem em `monthKey` (YYYY-MM). Útil para apurar
 * o impacto de caixa de um pedido projetado em um mês específico.
 */
export function cashImpactOnMonth(installments: ProjectedInstallment[], monthKey: string): number {
  let total = 0
  for (const inst of installments) {
    if (inst.due_date.slice(0, 7) === monthKey) total += inst.amount
  }
  return total
}

export interface CashCapItem {
  id: string
  /** Custo total da sugestão para este item (qty × unit_cost). */
  total_cost: number
  /** Prazos de pagamento do fornecedor em dias (vazio = à vista). */
  payment_terms: number[]
  urgencyRank: number
  abcRank: number
  score: number
}

/**
 * Variante do greedy que olha CAIXA DO MÊS (não valor de pedido). Cabe se a
 * soma das parcelas que cairiam em `monthKey` não estoura `cashRemaining`.
 *
 * Fornecedor com prazo longo (ex.: 30/60/90/120) joga grande parte para meses
 * seguintes, então um pedido caro pode caber mesmo num mês saturado.
 *
 * `today` é usado como base do `projectInstallments` (ex.: para escolher se a
 * 1ª parcela do prazo `5d` cai no mês corrente ou já entra no seguinte).
 */
export function splitByCashCap(
  items: CashCapItem[],
  cashRemaining: number,
  monthKey: string,
  today: Date,
): CapPartition {
  const sorted = [...items].sort((a, b) =>
    a.urgencyRank - b.urgencyRank
    || a.abcRank - b.abcRank
    || b.score - a.score
  )

  const fitsIds = new Set<string>()
  let fitsValue = 0
  let overflowsValue = 0
  let totalValue = 0
  let fitsCount = 0

  for (const it of sorted) {
    const projected = projectInstallments(it.total_cost, it.payment_terms, today)
    const cashImpact = cashImpactOnMonth(projected, monthKey)
    totalValue += cashImpact
    if (cashImpact <= 0 || fitsValue + cashImpact <= cashRemaining) {
      fitsIds.add(it.id)
      fitsValue += cashImpact
      fitsCount++
    } else {
      overflowsValue += cashImpact
    }
  }

  return { fitsIds, fitsValue, overflowsValue, totalValue, fitsCount, totalCount: items.length }
}

// ── Cap rolante: aloca a "virada" nos próximos meses com folga ───────────────

/** Primeiro dia (00:00 local) do mês `monthKey` (YYYY-MM). */
function firstDayOfMonth(monthKey: string): Date {
  const [y, m] = monthKey.split('-').map(Number)
  return new Date(y, m - 1, 1)
}

export interface MonthBudget {
  monthKey: string
  /** Folga inicial do mês (teto − parcelas já comprometidas). */
  remaining: number
}

export interface MonthAllocation {
  /** monthKey → ids alocados naquele mês. */
  assignment: Map<string, Set<string>>
  /** Itens que não couberam em nenhum mês da janela. */
  overflowIds: Set<string>
}

/**
 * Distribui itens nos próximos meses respeitando a folga de cada um (cap rolante).
 * Para cada item (ordem urgência → ABC → score), tenta o mês MAIS CEDO cuja folga
 * comporta: projeta as parcelas como se comprasse no 1º dia daquele mês e checa o
 * impacto de caixa em cada mês rastreado contra a folga RESTANTE. Encaixa e
 * decrementa; se não couber em nenhum, vai para `overflowIds`.
 *
 * Parcelas que caem além da janela `months` são "livres" (mesma simplificação do
 * splitByCashCap, que só olha o impacto dentro da janela).
 */
export function allocateAcrossMonths(
  items: CashCapItem[],
  months: MonthBudget[],
): MonthAllocation {
  const sorted = [...items].sort((a, b) =>
    a.urgencyRank - b.urgencyRank
    || a.abcRank - b.abcRank
    || b.score - a.score
  )

  const rem = new Map(months.map(m => [m.monthKey, m.remaining]))
  const keys = months.map(m => m.monthKey)
  const assignment = new Map<string, Set<string>>(keys.map(k => [k, new Set<string>()]))
  const overflowIds = new Set<string>()

  for (const it of sorted) {
    let placed = false
    for (const mk of keys) {
      const projected = projectInstallments(it.total_cost, it.payment_terms, firstDayOfMonth(mk))
      // Impacto por mês rastreado (meses fora da janela são ignorados).
      const impactByMonth = new Map<string, number>()
      for (const k of keys) {
        const imp = cashImpactOnMonth(projected, k)
        if (imp > 0) impactByMonth.set(k, imp)
      }
      // Cabe se a folga restante de TODOS os meses tocados cobre.
      const fits = [...impactByMonth].every(([k, imp]) => (rem.get(k) ?? 0) >= imp)
      if (fits) {
        for (const [k, imp] of impactByMonth) rem.set(k, (rem.get(k) ?? 0) - imp)
        assignment.get(mk)!.add(it.id)
        placed = true
        break
      }
    }
    if (!placed) overflowIds.add(it.id)
  }

  return { assignment, overflowIds }
}
